import { useEffect, useRef, useState } from "react";
import { transcribeAudio } from "./speech";
import { recordingMime } from "./voiceRecorder";

export type HandsFreeState =
  | "idle"
  | "requesting"
  | "listening"
  | "recording"
  | "transcribing"
  | "acting"
  | "paused";

export interface HandsFreeOptions<Context> {
  enabled: boolean;
  /** App speech, manual recording, and other microphone users pause this session. */
  paused: boolean;
  getContext: () => Context;
  onTranscript: (
    text: string,
    signal: AbortSignal,
    context: Context,
  ) => Promise<void>;
}

interface Snapshot {
  state: HandsFreeState;
  active: boolean;
  transcript: string;
  error: string;
}

const initial: Snapshot = {
  state: "idle",
  active: false,
  transcript: "",
  error: "",
};
const ECHO_COOLDOWN_MS = 800;
const SAMPLE_MS = 50;
// Leave room for natural pauses between a Hebrew street name, number and city.
const END_OF_SPEECH_MS = 1600;
const MAX_SPEECH_MS = 15_000;
// Includes pre-roll and stays below the server's decoded 20 second limit.
const MAX_CAPTURE_MS = 18_000;

/**
 * Foreground-only VAD capture. MediaRecorder starts before voice onset so short
 * Hebrew commands retain their first syllable. Silent buffers are discarded
 * locally; only a bounded utterance containing sustained sound goes to DEUS.
 */
class HandsFreeSession<Context> {
  private snapshot = { ...initial };
  private epoch = 0;
  private captureEpoch = 0;
  private stream: MediaStream | null = null;
  private audio: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private recorder: MediaRecorder | null = null;
  private sampleTimer: ReturnType<typeof setInterval> | null = null;
  private resumeTimer: ReturnType<typeof setTimeout> | null = null;
  private jobTimer: ReturnType<typeof setTimeout> | null = null;
  private job: AbortController | null = null;
  private acting = false;
  private paused = false;
  private cooldownUntil = 0;
  private failures = 0;
  private disposed = false;

  constructor(
    private options: () => HandsFreeOptions<Context>,
    private changed: (snapshot: Snapshot) => void,
  ) {}

  private update(patch: Partial<Snapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    if (!this.disposed) this.changed(this.snapshot);
  }

  private clearResume() {
    if (this.resumeTimer !== null) clearTimeout(this.resumeTimer);
    this.resumeTimer = null;
  }

  private abortJob() {
    this.job?.abort();
    this.job = null;
    if (this.jobTimer !== null) clearTimeout(this.jobTimer);
    this.jobTimer = null;
  }

  private discardCapture() {
    this.captureEpoch++;
    if (this.sampleTimer !== null) clearInterval(this.sampleTimer);
    this.sampleTimer = null;
    const recorder = this.recorder;
    this.recorder = null;
    if (recorder) {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      recorder.onerror = null;
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch {}
    }
  }

  private release() {
    this.discardCapture();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.analyser = null;
    void this.audio?.close().catch(() => {});
    this.audio = null;
  }

  private allowed() {
    return (
      this.snapshot.active &&
      this.options().enabled &&
      !this.options().paused &&
      !this.paused &&
      !document.hidden &&
      !this.disposed
    );
  }

  stop = () => {
    this.epoch++;
    this.clearResume();
    this.abortJob();
    this.acting = false;
    this.release();
    this.update({ active: false, state: "idle" });
  };

  clear = () => this.update({ transcript: "", error: "" });

  dispose() {
    this.disposed = true;
    this.stop();
  }

  sync() {
    if (!this.options().enabled) {
      this.stop();
      return;
    }
    const nextPaused = this.options().paused;
    if (nextPaused === this.paused) return;
    this.paused = nextPaused;
    if (!this.snapshot.active) return;
    if (nextPaused) {
      this.clearResume();
      this.cooldownUntil = Date.now() + ECHO_COOLDOWN_MS;
      // An acknowledgement generated by onTranscript must not cancel its own
      // report/search request. Pending recognition, however, is never replayed.
      if (!this.acting) {
        this.epoch++;
        this.abortJob();
      }
      this.release();
      this.update({ state: "paused" });
    } else {
      this.cooldownUntil = Date.now() + ECHO_COOLDOWN_MS;
      this.schedule();
    }
  }

  start = async () => {
    if (this.snapshot.active || !this.options().enabled || document.hidden)
      return;
    this.failures = 0;
    this.paused = this.options().paused;
    this.update({ active: true, error: "", transcript: "" });
    if (this.paused) {
      this.update({ state: "paused" });
      return;
    }
    await this.open();
  };

  private schedule(delay = 0) {
    this.clearResume();
    if (!this.allowed() || this.acting || this.job) return;
    this.update({ state: "paused" });
    this.resumeTimer = setTimeout(
      () => {
        this.resumeTimer = null;
        if (this.allowed()) void this.open();
      },
      Math.max(delay, this.cooldownUntil - Date.now(), 0),
    );
  }

  private fail(error: unknown) {
    const name = (error as Error)?.name;
    this.stop();
    this.update({
      error:
        name === "NotAllowedError" || name === "SecurityError"
          ? "נדרשת הרשאת מיקרופון להפעלה ללא מגע. הפעילו שוב לאחר מתן הרשאה."
          : (error as Error)?.message || "לא ניתן לפתוח את המיקרופון כרגע.",
    });
  }

  private async open() {
    if (!this.allowed() || this.job || this.acting) return;
    this.release();
    const run = ++this.epoch;
    this.update({ state: "requesting" });
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined" ||
      typeof AudioContext === "undefined"
    ) {
      this.fail(new Error("האזנה ללא מגע אינה נתמכת בדפדפן הזה."));
      return;
    }
    try {
      // Create/resume during the initial button gesture, before awaiting access.
      const audio = new AudioContext();
      this.audio = audio;
      const resumed = audio.resume();
      // Handle a fast resume rejection even while the permission dialog is open.
      void resumed.catch(() => {});
      const media = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      if (run !== this.epoch || !this.allowed()) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      this.stream = media;
      await resumed;
      if (run !== this.epoch || !this.allowed()) return;
      const analyser = audio.createAnalyser();
      analyser.fftSize = 1024;
      audio.createMediaStreamSource(media).connect(analyser);
      this.analyser = analyser;
      media.getTracks().forEach((track) => {
        track.onended = () => {
          if (run === this.epoch && this.snapshot.active)
            this.fail(
              new Error("המיקרופון נותק. הפעילו שוב את ההאזנה ללא מגע."),
            );
        };
      });
      this.capture(run);
    } catch (error) {
      if (run === this.epoch && this.snapshot.active) this.fail(error);
    }
  }

  private capture(run: number) {
    if (!this.allowed() || run !== this.epoch || !this.stream || !this.analyser)
      return;
    this.discardCapture();
    const captureRun = this.captureEpoch;
    const mime = recordingMime((m) => MediaRecorder.isTypeSupported(m));
    const recorder = new MediaRecorder(
      this.stream,
      mime ? { mimeType: mime } : undefined,
    );
    this.recorder = recorder;
    const chunks: Blob[] = [];
    const samples = new Uint8Array(this.analyser.fftSize);
    let size = 0;
    let voicedMs = 0;
    let lastVoice = 0;
    let speechBegan = 0;
    let context: Context;
    let hasContext = false;
    const began = Date.now();
    const current = () =>
      run === this.epoch && captureRun === this.captureEpoch && this.allowed();
    const finish = () => {
      if (this.sampleTimer !== null) clearInterval(this.sampleTimer);
      this.sampleTimer = null;
      try {
        if (recorder.state === "recording") recorder.stop();
      } catch (error) {
        if (current()) this.fail(error);
      }
    };
    recorder.ondataavailable = (event) => {
      if (!current() || !event.data.size) return;
      chunks.push(event.data);
      size += event.data.size;
      if (size > 2_000_000) finish();
    };
    recorder.onerror = () => {
      if (current())
        this.fail(new Error("ההקלטה נכשלה. הפעילו שוב את ההאזנה."));
    };
    recorder.onstop = () => {
      if (!current()) return;
      // Reject clicks, a completely silent buffer, and oversized recordings.
      if (voicedMs < 150 || !hasContext || size < 100 || size > 2_097_152) {
        try {
          this.capture(run);
        } catch (error) {
          this.fail(error);
        }
        return;
      }
      const blob = new Blob(chunks, {
        type: recorder.mimeType || mime || "audio/webm",
      });
      this.release();
      void this.recognize(run, blob, context);
    };
    recorder.start(200);
    this.update({ state: "listening" });
    this.sampleTimer = setInterval(() => {
      if (!current() || !this.analyser) return;
      this.analyser.getByteTimeDomainData(samples);
      let energy = 0;
      for (const sample of samples) energy += ((sample - 128) / 128) ** 2;
      const speaking = Math.sqrt(energy / samples.length) > 0.018;
      const now = Date.now();
      if (speaking) {
        voicedMs += SAMPLE_MS;
        lastVoice = now;
        if (!hasContext) {
          speechBegan = now;
          context = this.options().getContext();
          hasContext = true;
          this.update({ state: "recording", error: "" });
        }
      }
      if (
        (voicedMs >= 150 && now - lastVoice >= END_OF_SPEECH_MS) ||
        (hasContext && now - speechBegan >= MAX_SPEECH_MS) ||
        now - began >= MAX_CAPTURE_MS ||
        (voicedMs < 150 && now - began >= 3000 && now - lastVoice >= END_OF_SPEECH_MS)
      )
        finish();
    }, SAMPLE_MS);
  }

  private async recognize(run: number, blob: Blob, context: Context) {
    if (run !== this.epoch || !this.allowed()) return;
    const controller = new AbortController();
    this.job = controller;
    this.update({ state: "transcribing" });
    const timeout = setTimeout(() => controller.abort(), 90_000);
    this.jobTimer = timeout;
    try {
      const text = await transcribeAudio(blob, controller.signal);
      clearTimeout(timeout);
      if (run !== this.epoch || !this.allowed() || controller.signal.aborted)
        return;
      this.failures = 0;
      this.acting = true;
      this.update({ state: "acting", transcript: text });
      await this.options().onTranscript(text, controller.signal, context);
    } catch (error) {
      if (run === this.epoch && this.snapshot.active) {
        // A valid transcript can still fail to report (for example no GPS).
        // Those are action errors, not a DEUS outage, and must not disable mic.
        if (!this.acting) this.failures++;
        this.update({
          error:
            (error as Error)?.name === "AbortError"
              ? "זיהוי הדיבור ארך יותר מדי זמן. ההאזנה תנסה שוב."
              : (error as Error)?.message || "זיהוי הדיבור לא הצליח. נסו שוב.",
        });
      }
    } finally {
      clearTimeout(timeout);
      if (this.jobTimer === timeout) this.jobTimer = null;
      if (run === this.epoch && this.snapshot.active) {
        this.job = null;
        this.acting = false;
        // Avoid an upload/retry loop during an outage or sustained cabin noise.
        if (this.failures >= 3) {
          this.stop();
          this.update({
            error:
              "זיהוי הדיבור נכשל שלוש פעמים. בדקו את החיבור והפעילו שוב את ההאזנה.",
          });
        } else this.schedule(this.failures ? 1500 : 0);
      }
    }
  }
}

/** Enabling a preference does not open the mic: start() must follow a user gesture. */
export function useHandsFreeRecorder<Context>(
  options: HandsFreeOptions<Context>,
) {
  const latest = useRef(options);
  latest.current = options;
  const [snapshot, setSnapshot] = useState<Snapshot>(initial);
  const session = useRef<HandsFreeSession<Context> | null>(null);
  if (!session.current)
    session.current = new HandsFreeSession(() => latest.current, setSnapshot);
  useEffect(() => {
    const current = (session.current ??= new HandsFreeSession(
      () => latest.current,
      setSnapshot,
    ));
    const hidden = () => {
      if (document.hidden) current.stop();
    };
    const stopped = () => current.stop();
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("derech:media-stopped", stopped);
    return () => {
      current.dispose();
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("derech:media-stopped", stopped);
      // React StrictMode remounts effects using the same ref.
      session.current = null;
    };
  }, []);
  useEffect(() => {
    session.current?.sync();
  }, [options.enabled, options.paused]);
  return {
    ...snapshot,
    pauseReason:
      snapshot.active && snapshot.state === "paused"
        ? options.paused
          ? ("audio" as const)
          : ("cooldown" as const)
        : null,
    start: () => session.current?.start() ?? Promise.resolve(),
    stop: () => session.current?.stop(),
    clear: () => session.current?.clear(),
  };
}
